import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ProvisioningStreamEvent } from "Api/organizations/projects/streamProvisioning.request";

import {
  deleteProject,
  getProject,
  getProjects,
  updateProject
} from "./thunks";

import type { GitProvider } from "Reducers/projectCreation/projectCreation.types";

import type {
  ProjectsState,
  ProvisioningStep,
  StreamConnectionStatus
} from "./types";

const computeNextSteps = (
  previousSteps: ReadonlyArray<ProvisioningStep>,
  event: { stage: string; label: string },
  terminalStatus: "in_progress" | "failed"
): ProvisioningStep[] => {
  const last = previousSteps[previousSteps.length - 1];
  const terminalStep: ProvisioningStep = {
    stage: event.stage,
    label: event.label,
    status: terminalStatus
  };

  // Same stage repeats (label may have refined): replace the trailing entry.
  // New stage: mark the previous in-progress entry as done, then append.
  if (last?.stage === event.stage) {
    return [...previousSteps.slice(0, -1), terminalStep];
  }
  return [
    ...previousSteps.map((step): ProvisioningStep =>
      step.status === "in_progress" ? { ...step, status: "done" } : step
    ),
    terminalStep
  ];
};

const initialState: ProjectsState = {
  data: {},
  provisioning: {},
  streamConnections: {},
  pendingGithubIntegrations: {},
  wizardGitProviders: {},
  seenIntegrationActivities: {},
  regionalRetryInProgress: {},
  organizationsProvisioningProject: [],
  provisioningCompleting: [],
  firstProjectSetupOrganizationId: null,
  loading: null,
  getProjectError: null
};

const organizationProjectsReducer = createSlice({
  name: "app/organization/projects",
  initialState,
  reducers: {
    startProvisioningPulling: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      state.organizationsProvisioningProject.push(
        action.payload.organizationId
      );
    },
    endProvisioningPulling: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      state.organizationsProvisioningProject =
        state.organizationsProvisioningProject.filter(
          id => id !== action.payload.organizationId
        );
    },
    setFirstProjectSetupOrganization: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.firstProjectSetupOrganizationId = action.payload;
    },
    startProvisioningCompletion: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      state.provisioningCompleting.push(action.payload.organizationId);
    },
    clearProvisioningCompletion: (
      state,
      action: PayloadAction<{ organizationId: string }>
    ) => {
      state.provisioningCompleting = state.provisioningCompleting.filter(
        id => id !== action.payload.organizationId
      );
    },
    applyProvisioningEvent: (
      state,
      action: PayloadAction<{
        organizationUuid: string;
        event: ProvisioningStreamEvent;
      }>
    ) => {
      const { organizationUuid, event } = action.payload;
      if (event.type === "keep_alive") return;

      if (!state.provisioning[organizationUuid]) {
        state.provisioning[organizationUuid] = {};
      }
      const orgSlots = state.provisioning[organizationUuid];

      if (event.type === "data") {
        const previous = orgSlots[event.project_id];
        orgSlots[event.project_id] = {
          currentStage: event.stage,
          currentMessage: event.label,
          steps: computeNextSteps(previous?.steps ?? [], event, "in_progress"),
          plan: event.steps,
          streamStatus: "active"
        };
        return;
      }

      if (event.type === "done") {
        const previous = orgSlots[event.project_id];
        if (!previous) return;
        // Keep the slot alive past `done` so the panel keeps painting the
        // completed timeline until `getProject.fulfilled` flips the project's
        // status off `requested`. Without this, the panel briefly renders
        // with no steps while the regional fetch is in flight.
        orgSlots[event.project_id] = {
          ...previous,
          steps: previous.steps.map((step): ProvisioningStep =>
            step.status === "in_progress" ? { ...step, status: "done" } : step
          )
        };
        return;
      }

      if (event.type === "error") {
        const previous = orgSlots[event.project_id];
        orgSlots[event.project_id] = {
          currentStage: event.stage,
          currentMessage: event.label,
          steps: computeNextSteps(previous?.steps ?? [], event, "failed"),
          plan: previous?.plan ?? null,
          streamStatus: "error",
          error: { stage: event.stage, reason: event.reason }
        };
      }
    },
    setStreamConnectionStatus: (
      state,
      action: PayloadAction<{
        organizationUuid: string;
        status: StreamConnectionStatus;
      }>
    ) => {
      state.streamConnections[action.payload.organizationUuid] =
        action.payload.status;
    },
    clearStreamConnection: (
      state,
      action: PayloadAction<{ organizationUuid: string }>
    ) => {
      delete state.streamConnections[action.payload.organizationUuid];
    },
    markPendingGithubIntegration: (
      state,
      action: PayloadAction<{ projectId: string; expiresAt: number }>
    ) => {
      state.pendingGithubIntegrations[action.payload.projectId] = {
        expiresAt: action.payload.expiresAt
      };
    },
    clearPendingGithubIntegration: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      delete state.pendingGithubIntegrations[action.payload.projectId];
    },
    setWizardGitProvider: (
      state,
      action: PayloadAction<{
        projectId: string;
        provider: NonNullable<GitProvider>;
      }>
    ) => {
      state.wizardGitProviders[action.payload.projectId] =
        action.payload.provider;
    },
    clearWizardGitProvider: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      delete state.wizardGitProviders[action.payload.projectId];
    },
    markIntegrationActivitiesSeen: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      state.seenIntegrationActivities[action.payload.projectId] = true;
    },
    clearIntegrationActivitiesSeen: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      delete state.seenIntegrationActivities[action.payload.projectId];
    },
    clearProvisioningSlot: (
      state,
      action: PayloadAction<{ organizationUuid: string; projectId: string }>
    ) => {
      const slots = state.provisioning[action.payload.organizationUuid];
      if (slots) delete slots[action.payload.projectId];
    },
    markRegionalRetryStart: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      state.regionalRetryInProgress[action.payload.projectId] = true;
    },
    markRegionalRetryEnd: (
      state,
      action: PayloadAction<{ projectId: string }>
    ) => {
      delete state.regionalRetryInProgress[action.payload.projectId];
    }
  },
  extraReducers: builder => {
    builder
      // GET PROJECTS
      .addCase(getProjects.pending, state => {
        state.loading = true;
      })
      .addCase(getProjects.fulfilled, (state, { meta, payload }) => {
        const { organizationId } = meta.arg;
        if (!state.data[organizationId]) state.data[organizationId] = {};
        for (const project of payload.items) {
          state.data[organizationId][project.id] = project;
        }

        state.loading = false;
      })
      .addCase(getProjects.rejected, state => {
        state.loading = false;
      })
      // GET PROJECT
      .addCase(getProject.pending, state => {
        state.loading = true;
        state.getProjectError = null;
      })
      .addCase(getProject.fulfilled, (state, { meta, payload }) => {
        const { organizationId, projectId } = meta.arg;
        if (!state.data[organizationId]) state.data[organizationId] = {};
        state.data[organizationId][projectId] = payload;
        state.loading = false;
        state.getProjectError = null;

        // Slot stays alive past the stream `done` AND past this fulfilled
        // event — the listener drops it once both the regional project and
        // its environments have been fetched. Without that handshake, the
        // panel unmounts before the dashboard data lands and the generic
        // "Upsun engine initializing" loader leaks through.
      })
      .addCase(getProject.rejected, (state, { meta, payload }) => {
        state.loading = false;
        state.getProjectError = {
          organizationId: meta.arg.organizationId,
          projectId: meta.arg.projectId,
          error: payload
        };
      })
      // UPDATE PROJECT
      .addCase(updateProject.fulfilled, (state, { meta, payload }) => {
        const { organizationId, projectId } = meta.arg;
        if (!state.data[organizationId]) state.data[organizationId] = {};
        state.data[organizationId][projectId] = payload;
      })
      // Remove the deleted project from state so consumers stop seeing it as
      // active even if the caller doesn't redirect away.
      .addCase(deleteProject.fulfilled, (state, { meta }) => {
        const { organizationId, projectId } = meta.arg;
        if (state.data[organizationId]) {
          delete state.data[organizationId][projectId];
        }
      });
  }
});

export const {
  startProvisioningPulling,
  endProvisioningPulling,
  setFirstProjectSetupOrganization,
  startProvisioningCompletion,
  clearProvisioningCompletion,
  applyProvisioningEvent,
  setStreamConnectionStatus,
  clearStreamConnection,
  markPendingGithubIntegration,
  clearPendingGithubIntegration,
  setWizardGitProvider,
  clearWizardGitProvider,
  markIntegrationActivitiesSeen,
  clearIntegrationActivitiesSeen,
  clearProvisioningSlot,
  markRegionalRetryStart,
  markRegionalRetryEnd
} = organizationProjectsReducer.actions;

export default organizationProjectsReducer.reducer;
